import { useCallback, useMemo } from 'react';
import type { GetServiceProviderResult } from '@pflegenavi/service-provider-frontend/api';
import { useGetServiceProviderPhoenix } from '@pflegenavi/service-provider-frontend/api';

export interface BankAccountStatus {
  // The recipient exists and can receive payouts.
  isReady: boolean;
  // Recipient created but SCA not completed yet (e.g. started on mobile, never confirmed).
  recipientPending: boolean;
  scaUrl: string | null | undefined;
  openScaUrl: () => void;
}

/**
 * MangoPay sets `mangopay_bank_account_id` when the recipient is created, which is
 * *before* the user completes SCA. A stored id therefore does not mean the account
 * is usable — only the recipient status does, which is also what the backend gates
 * payouts and batch submission on (`ServiceProvider.onboarding_complete?/1`).
 */
export const deriveBankAccountStatus = (
  data: GetServiceProviderResult['data'] | undefined
): Omit<BankAccountStatus, 'openScaUrl'> => {
  const recipientStatus = data?.mangopay_recipient_status;

  return {
    isReady: recipientStatus === 'ACTIVE',
    recipientPending: recipientStatus === 'PENDING',
    scaUrl: data?.mangopay_sca_url,
  };
};

export const useBankAccountStatus = (): BankAccountStatus => {
  const { data: serviceProvider } = useGetServiceProviderPhoenix();
  const status = useMemo(
    () => deriveBankAccountStatus(serviceProvider?.data),
    [serviceProvider?.data]
  );

  // MangoPay rejects an SCA link opened without a returnUrl
  // ("Please provide a valid return url"), so append it every time we reopen.
  const { scaUrl } = status;
  const openScaUrl = useCallback(() => {
    if (!scaUrl) {
      return;
    }
    const returnUrl = `${window.location.origin}${window.location.pathname}`;
    const separator = scaUrl.includes('?') ? '&' : '?';
    window.open(
      `${scaUrl}${separator}returnUrl=${encodeURIComponent(returnUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
  }, [scaUrl]);

  return { ...status, openScaUrl };
};
